import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, getAdminTeams, type AdminFilter, type AdminTeamRow, type AdminTeamsPage } from "../api.js";
import { AppShell } from "../components/AppShell.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { AdminNotFound, AdminTag, Chip } from "./AdminChrome.js";
import { latestTone, seen, toneClass, usd } from "./format.js";

const FILTERS: [AdminFilter, string][] = [
  ["all", "All"],
  ["stuck", "Stuck"],
  ["paid", "Paid"],
  ["free_forever", "Free forever"],
  ["recent", "Last 7 days"],
];

const COLUMNS: [label: string, numeric: boolean][] = [
  ["Team · person", false],
  ["Last seen", false],
  ["Latest", false],
  ["Films", true],
  ["Tried", true],
  ["Paid", true],
  ["Balance", true],
  ["Our cost", true],
];

const errorText = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** Every team, most recently seen first. The API decides who may see it; a 404 here is the answer for everybody else. */
export const AdminCustomers: React.FC = () => {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<AdminFilter>("all");
  const [teams, setTeams] = useState<AdminTeamRow[]>([]);
  const [counts, setCounts] = useState<AdminTeamsPage["counts"] | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  // Search as you type, but ask once the typing stops.
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(input.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [input]);

  /**
   * Which query is current. Bumped whenever q or filter changes; every request
   * remembers the one it was made for and drops its answer, and its loading
   * state, if that is no longer current. A "Load more" in flight when the
   * filter changes must not append the old filter's rows to the new list.
   */
  const generation = useRef(0);

  const fail = (caught: unknown) => {
    if (caught instanceof ApiError && caught.status === 404) setNotFound(true);
    else setError(errorText(caught));
  };

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    getAdminTeams({ q, filter })
      .then((page) => {
        if (generation.current !== mine) return;
        setTeams(page.teams);
        setCounts(page.counts);
        setNextOffset(page.nextOffset);
      })
      .catch((caught: unknown) => {
        if (generation.current === mine) fail(caught);
      })
      .finally(() => {
        if (generation.current === mine) setLoading(false);
      });
  }, [q, filter]);

  // On leaving the page, nothing still in flight may set state.
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );

  const loadMore = async () => {
    if (nextOffset === null || loading) return;
    const mine = generation.current;
    setLoading(true);
    setError(null);
    try {
      const page = await getAdminTeams({ q, filter, offset: nextOffset });
      if (generation.current !== mine) return;
      setTeams((current) => [...current, ...page.teams]);
      setNextOffset(page.nextOffset);
    } catch (caught) {
      if (generation.current === mine) fail(caught);
    } finally {
      // A newer query owns `loading` now, and clears it itself.
      if (generation.current === mine) setLoading(false);
    }
  };

  if (notFound) return <AdminNotFound />;

  return (
    <AppShell
      wide
      breadcrumb={
        <>
          <span className="font-medium text-ink-100">Admin</span>
          <AdminTag />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <h1 className="text-[21px] font-semibold text-ink-100">Customers</h1>

        <div className="flex flex-wrap gap-1 border-b border-[var(--edge)]">
          <span
            aria-current="page"
            className="-mb-px border-b-2 border-ember-400 px-3 py-2 text-[13px] font-semibold text-ink-100"
          >
            Customers
          </span>
          {["Funnel", "Money"].map((tab) => (
            <span key={tab} className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-ink-400">
              {tab}
              <span className="rounded-full bg-bay-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-400">
                later
              </span>
            </span>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            className="input min-w-0 flex-[1_1_240px]"
            placeholder="Search email, name, team or website…"
            aria-label="Search customers"
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          {FILTERS.map(([value, label]) => (
            <Chip key={value} on={filter === value} onClick={() => setFilter(value)}>
              {label}
              {counts ? ` · ${counts[value]}` : ""}
            </Chip>
          ))}
        </div>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {/* Only the table scrolls sideways; the page stays put on a phone. */}
        <div className="overflow-x-auto rounded-[10px] border border-[var(--edge)] bg-bay-850">
          <table className="w-full min-w-[860px] border-collapse text-[13px]">
            <thead>
              <tr>
                {COLUMNS.map(([label, numeric]) => (
                  <th
                    key={label}
                    scope="col"
                    className={`whitespace-nowrap border-b border-[var(--edge)] bg-bay-900 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-400 ${numeric ? "text-right" : "text-left"}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.orgId} className="border-b border-[var(--edge)] last:border-b-0 hover:bg-bay-900">
                  <td className="px-3 py-[11px] align-top">
                    <Link
                      to={`/admin/teams/${encodeURIComponent(team.orgId)}`}
                      className="flex flex-col gap-0.5 hover:underline"
                    >
                      <b className="font-semibold text-ink-100">{team.name ?? team.email}</b>
                      <small className="text-[12px] text-ink-400">
                        {team.email}
                        {team.members > 1 ? ` · ${team.members} people` : ""}
                        {team.freeForever ? " · free forever" : ""}
                      </small>
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-[11px] align-top text-ink-300">{seen(team.lastSeen)}</td>
                  <td className="px-3 py-[11px] align-top">
                    <span
                      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${toneClass[latestTone(team.latest)]}`}
                    >
                      {team.latest?.label ?? "Signed up"}
                    </span>
                  </td>
                  <td className="px-3 py-[11px] text-right align-top tabular-nums">{team.films}</td>
                  <td className="px-3 py-[11px] text-right align-top tabular-nums">{team.attempts}</td>
                  <td className="px-3 py-[11px] text-right align-top tabular-nums">{usd(team.paidUsd)}</td>
                  <td
                    className={`px-3 py-[11px] text-right align-top tabular-nums ${team.balanceUsd < 0 ? "text-[#b91c1c]" : ""}`}
                  >
                    {usd(team.balanceUsd)}
                  </td>
                  <td className="px-3 py-[11px] text-right align-top tabular-nums">{usd(team.costUsd)}</td>
                </tr>
              ))}
              {!loading && teams.length === 0 && !error ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-ink-300">
                    No teams match.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-center gap-2">
          {loading ? <Spinner /> : null}
          {nextOffset !== null && !loading ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void loadMore()}>
              Load more
            </button>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
};
